import React, { useContext, useEffect, useState, useMemo } from 'react';
import { ShopContext } from '../context/ShopContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { FiFilter, FiChevronLeft, FiChevronRight, FiGrid, FiList, FiAlertCircle, FiRefreshCw, FiLoader } from 'react-icons/fi';
import Title from '../components/Title';
import FiltersPanel from '../components/FiltersPanel';
import AppliedFilters from '../components/AppliedFilters';
import ProductCard from '../components/ProductCard';
import ProductListItem from '../components/ProductListItem';
import Pagination from '../components/Pagination';
import ViewToggle from '../components/ViewToggle';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

const Collection = () => {
  const {
    products,
    search,
    setSearch,
    setShowSearch,
    reviews,
    backendUrl,
  } = useContext(ShopContext) || {};

  const productsPerPage = 12;
  

  const [showFilter, setShowFilter] = useState(window.innerWidth > 768);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryItems, setCategoryItems] = useState([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubItem, setSelectedSubItem] = useState('');
  const [sortType, setSortType] = useState('relevant');
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewType, setViewType] = useState('grid');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFilters, setExpandedFilters] = useState({
    sort: true,
    category: true,
    price: true,
    color: true,
    material: true,
  });

  // Get available filters from products
  const availableFilters = useMemo(() => {
    if (!products) return { colors: [], materials: [] };

    const colors = new Set();
    const materials = new Set();

    products.forEach((product) => {
      product.colors?.forEach((color) => colors.add(color));
      product.materials?.forEach((material) => materials.add(material));
    });

    return {
      colors: Array.from(colors).sort(),
      materials: Array.from(materials).sort(),
    };
  }, [products]);

  // Count applied filters
  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (search) count++;
    if (selectedMainCategory) count++;
    if (selectedSubItem) count++;
    if (priceRange[0] > 0 || priceRange[1] < 10000) count++;
    count += selectedColors.length;
    count += selectedMaterials.length;
    return count;
  }, [search, selectedMainCategory, selectedSubItem, priceRange, selectedColors, selectedMaterials]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${backendUrl}/api/productCategories`);
        setCategories(response.data);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, [backendUrl]);

  // Fetch sub-items when main category changes
useEffect(() => {
  if (selectedMainCategory) {
    const selected = categories.find(cat => cat._id === selectedMainCategory);
    setCategoryItems(selected?.children || []);
  } else {
    setCategoryItems([]);
    setSelectedSubItem('');
  }
}, [selectedMainCategory, categories]);


  // Filter and sort products
  useEffect(() => {
    if (products) {
      let result = [...products];
      

      // Apply search filter
      if (search) {
        const searchTerm = search.toLowerCase();
        result = result.filter(
          (product) =>
            product.name.toLowerCase().includes(searchTerm) ||
            product.description.toLowerCase().includes(searchTerm) ||
            product.colors.some((color) => color.toLowerCase().includes(searchTerm)) ||
            product.materials.some((material) => material.toLowerCase().includes(searchTerm))
        );
      }

      // Apply category filters
      if (selectedMainCategory) {
        result = result.filter((product) => product.category === selectedMainCategory);
      }
      if (selectedSubItem) {
        result = result.filter((product) => product.subCategory === selectedSubItem);
      }

      // Apply price filter
      result = result.filter(
        (product) => product.price >= priceRange[0] && product.price <= priceRange[1]
      );

      // Apply color filter
      if (selectedColors.length > 0) {
        result = result.filter((product) =>
          product.colors?.some((color) => selectedColors.includes(color))
        );
      }

      // Apply material filter
      if (selectedMaterials.length > 0) {
        result = result.filter((product) =>
          product.materials?.some((material) => selectedMaterials.includes(material))
        );
      }

      // Apply sorting
      switch (sortType) {
        case 'newest':
          result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          break;
        case 'price-low-high':
          result.sort((a, b) => a.price - b.price);
          break;
        case 'price-high-low':
          result.sort((a, b) => b.price - a.price);
          break;
        case 'rating':
          result.sort((a, b) => {
            const ratingA = getAverageRating(a._id);
            const ratingB = getAverageRating(b._id);
            return ratingB - ratingA;
          });
          break;
        case 'popularity':
          // Assuming popularity is based on sales or views
          result.sort((a, b) => (b.views || 0) - (a.views || 0));
          break;
        case 'a-z':
          result.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'z-a':
          result.sort((a, b) => b.name.localeCompare(a.name));
          break;
        default: // 'relevant'
          // Default sorting (could be based on relevance algorithm)
          break;
      }

      setFilteredProducts(result);
      setCurrentPage(1); // Reset to first page when filters change
    }
  }, [
    products,
    search,
    selectedMainCategory,
    selectedSubItem,
    priceRange,
    selectedColors,
    selectedMaterials,
    sortType,
    reviews,
  ]);

  // Calculate average rating for a product
  const getAverageRating = (productId) => {
    const productReviews = reviews.filter((review) => review.productId === productId);
    if (productReviews.length === 0) return 0;
    const sum = productReviews.reduce((acc, review) => acc + review.rating, 0);
    return sum / productReviews.length;
  };

  // Format price with currency
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Toggle filter section expansion
  const toggleFilterSection = (section) => {
    setExpandedFilters((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Toggle filter selection (colors, materials)
  const toggleFilterSelection = (type, value) => {
    if (type === 'color') {
      setSelectedColors((prev) =>
        prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
      );
    } else if (type === 'material') {
      setSelectedMaterials((prev) =>
        prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value]
      );
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearch('');
    setShowSearch(false);
    setSelectedMainCategory('');
    setSelectedSubItem('');
    setPriceRange([0, 10000]);
    setSelectedColors([]);
    setSelectedMaterials([]);
    setSortType('relevant');
  };

  // Handle window resize for filter panel
  useEffect(() => {
    const handleResize = () => {
      setShowFilter(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  if (isLoading && !products) {
    return <LoadingSpinner fullPage />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="flex flex-col lg:flex-row pt-10 border-t relative">
      <Helmet>
        <title>Shop Curtains & Blinds Online | Collection | Sahla Curtain Center</title>
        <meta name="description" content="Discover the latest curtain and blind collections at Sahla Curtain Center. Shop premium window treatments, drapes, and home decor in Kerala. Fast delivery, best prices, and expert advice." />
        <meta name="keywords" content="curtains, blinds, drapes, window treatments, home decor, Kerala, Sahla Curtain Center, buy curtains online, premium curtains, curtain shop, collection" />
        <link rel="canonical" href="https://sccstore.com/collection" />
        {/* Open Graph */}
        <meta property="og:title" content="Shop Curtains & Blinds Online | Collection | Sahla Curtain Center" />
        <meta property="og:description" content="Browse our premium collection of curtains, blinds, and home decor. Find the perfect style for your home at Sahla Curtain Center." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sccstore.com/collection" />
        <meta property="og:image" content="https://sccstore.com/preview.jpg" />
        <meta property="og:site_name" content="Sahla Curtain Center" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Shop Curtains & Blinds Online | Collection | Sahla Curtain Center" />
        <meta name="twitter:description" content="Discover the latest curtain and blind collections at Sahla Curtain Center. Shop premium window treatments, drapes, and home decor in Kerala." />
        <meta name="twitter:image" content="https://sccstore.com/preview.jpg" />
        <meta name="twitter:site" content="@sccstore" />
        {/* Social Profiles */}
        <meta property="og:see_also" content="https://www.instagram.com/sahlacurtaincenter/" />
        <meta property="og:see_also" content="https://www.facebook.com/people/Sahla-Curtain-Center/61565451845405/" />
        <meta property="og:see_also" content="https://www.youtube.com/@sahlacurtaincenter" />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Curtain & Blind Collection",
            "description": "Browse the latest curtain and blind collections at Sahla Curtain Center.",
            "url": "https://sccstore.com/collection",
            "image": "https://sccstore.com/preview.jpg",
            "publisher": {
              "@type": "Organization",
              "name": "Sahla Curtain Center",
              "logo": {
                "@type": "ImageObject",
                "url": "https://sccstore.com/favicon.png"
              },
              "sameAs": [
                "https://www.instagram.com/sahlacurtaincenter/",
                "https://www.facebook.com/people/Sahla-Curtain-Center/61565451845405/",
                "https://www.youtube.com/@sahlacurtaincenter"
              ]
            }
          }
        `}</script>
      </Helmet>

      {/* Mobile Header */}
      <div className="lg:hidden flex justify-between items-center px-4 mb-4">
        <Title text1="All" text2="COLLECTIONS" />
        <div className="flex items-center gap-3">
          <ViewToggle viewType={viewType} setViewType={setViewType} />
          <button
            onClick={() => setShowFilter(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg"
          >
            <FiFilter className="text-lg" />
            <span>Filters</span>
            {appliedFiltersCount > 0 && (
              <span className="bg-black text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {appliedFiltersCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      <FiltersPanel
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        expandedFilters={expandedFilters}
        toggleFilterSection={toggleFilterSection}
        sortType={sortType}
        setSortType={setSortType}
        categories={categories}
        selectedMainCategory={selectedMainCategory}
        setSelectedMainCategory={setSelectedMainCategory}
        categoryItems={categoryItems}
        selectedSubItem={selectedSubItem}
        setSelectedSubItem={setSelectedSubItem}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        formatPrice={formatPrice}
        availableFilters={availableFilters}
        selectedColors={selectedColors}
        selectedMaterials={selectedMaterials}
        toggleFilterSelection={toggleFilterSelection}
        appliedFiltersCount={appliedFiltersCount}
        resetFilters={resetFilters}
        filteredProducts={filteredProducts}
      />

      {/* Product Display Section */}
      <div className="flex-1 px-4 lg:px-0">
        {/* Desktop Header */}
        <div className="hidden lg:flex justify-between items-center mb-4">
          <Title text1="All" text2="COLLECTIONS" />
          <div className="flex items-center gap-4">
            {search && (
              <div className="text-sm text-gray-600">
                {filteredProducts.length} results found
              </div>
            )}
            <ViewToggle viewType={viewType} setViewType={setViewType} />
          </div>
        </div>

        {/* Applied Filters */}
        <AppliedFilters
          search={search}
          setSearch={setSearch}
          setShowSearch={setShowSearch}
          selectedMainCategory={selectedMainCategory}
          setSelectedMainCategory={setSelectedMainCategory}
          categories={categories}
          selectedSubItem={selectedSubItem}
          setSelectedSubItem={setSelectedSubItem}
          categoryItems={categoryItems}
          priceRange={priceRange}
          setPriceRange={setPriceRange}
          formatPrice={formatPrice}
          selectedColors={selectedColors}
          toggleFilterSelection={toggleFilterSelection}
          selectedMaterials={selectedMaterials}
        />

        {/* Products Grid/List View */}
        {paginatedProducts.length > 0 ? (
          viewType === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {paginatedProducts.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  formatPrice={formatPrice}
                  getAverageRating={getAverageRating}
                  reviews={reviews}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {paginatedProducts.map((product) => (
                <ProductListItem
                  key={product._id}
                  product={product}
                  formatPrice={formatPrice}
                  getAverageRating={getAverageRating}
                  reviews={reviews}
                  categories={categories}
                  categoryItems={categoryItems}
                />
              ))}
            </div>
          )
        ) : (
          <div className="text-center py-12">
            <h3 className="text-xl font-medium mb-2">No products found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your filters or search term
            </p>
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
            >
              Reset All Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {filteredProducts.length > productsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default Collection;